import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api.ts';
import { formatEuros } from '../format.ts';
import { PAYMENT_ERRORS, getStripe } from '../stripeClient.ts';

export interface PaymentInfo {
  status: 'PENDING' | 'AUTHORIZED' | 'CAPTURED' | 'FAILED' | 'REFUNDED' | 'CANCELLED';
  authorizeAt: string | null;
  failureMessage: string | null;
  amountCents: number;
}

/** Où en est le paiement de la mission, pour l'établissement : planifié, bloqué, refusé, réglé. */
export function PaymentStatus({ missionId, payment, onChanged }: { missionId: string; payment: PaymentInfo; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function retry() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ status: string; clientSecret?: string }>(`/payments/missions/${missionId}/retry-authorization`);
      if (res.status === 'REQUIRES_ACTION' && res.clientSecret) {
        const stripe = await getStripe();
        const result = await stripe?.handleNextAction({ clientSecret: res.clientSecret });
        if (result?.error) throw new Error(result.error.message);
      }
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? PAYMENT_ERRORS[err.code] ?? 'Nouvelle tentative impossible.' : err instanceof Error ? err.message : 'Nouvelle tentative impossible.');
    } finally {
      setBusy(false);
    }
  }

  const amount = formatEuros(payment.amountCents);
  const when = payment.authorizeAt ? new Date(payment.authorizeAt).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '';

  if (payment.status === 'CAPTURED') return <div className="badge badge-neutral" style={{ justifyContent: 'center', padding: '10px 14px' }}>Paiement de {amount} réglé</div>;
  if (payment.status === 'AUTHORIZED') {
    return <div className="badge badge-available" style={{ justifyContent: 'center', padding: '10px 14px', lineHeight: 1.5 }}>{amount} bloqués sur votre carte (sans débit) · débités à la validation des heures</div>;
  }
  if (payment.status === 'PENDING') {
    const needsAction = !payment.authorizeAt || new Date(payment.authorizeAt).getTime() <= Date.now();
    return (
      <div className="badge badge-pending" style={{ padding: '10px 14px', lineHeight: 1.5, flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
        <span>{needsAction ? `Le blocage de ${amount} sur votre carte est en cours.` : `Le blocage de ${amount} sur votre carte est prévu le ${when}. Vous ne serez débité qu'à la validation des heures.`}</span>
        {needsAction && <button className="btn btn-primary btn-sm" onClick={retry} disabled={busy}>Finaliser le paiement</button>}
      </div>
    );
  }
  if (payment.status === 'FAILED') {
    return (
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8, borderColor: 'var(--danger)' }}>
        <div style={{ fontWeight: 800, color: 'var(--danger)' }}>Le paiement n'a pas pu être sécurisé</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{payment.failureMessage ?? 'La carte a été refusée.'} Sans carte valide, la mission risque d'être annulée.</div>
        {error && <div className="error-banner">{error}</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to={`/resto/carte?retour=/resto/missions/${missionId}`} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }}>Changer de carte</Link>
          <button className="btn btn-primary btn-sm" onClick={retry} disabled={busy}>Relancer le paiement</button>
        </div>
      </div>
    );
  }
  return null;
}
